// src/components/visuals/TwoRangesOverlap.js
import React, { useMemo } from "react";
import { InlineMath } from "react-katex";

/**
 * Dwa zestawy zakresów na JEDNEJ osi i na DWÓCH wysokościach (stacked).
 * Zestaw A (pomarańczowy, wyżej) oraz zestaw B (niebieski, niżej).
 * Obsługuje:
 *  - stare API: ranges = [rangeA, rangeB]  (pojedyncze odcinki)
 *  - nowe API:  ranges = [[segA1, segA2,...], [segB1, segB2,...]]  (multi)
 *
 * Każda część wspólna (A∩B) jest zacieniona osobnym prostokątem.
 *
 * Segment: { from, to, leftClosed, rightClosed, leftLabelLatex?, rightLabelLatex? }
 *
 * iOS/mobile fix:
 *  - ułamki \\dfrac/\\frac (także z minusem i liczby mieszane) rysujemy jako SVG,
 *    żeby nie rozjeżdżały się podpisy na Safari iOS.
 */

const OVERLAP_PRESETS = {
  pink: "#ff4fa3",
  fuchsia: "#d946ef",
  violet: "#a78bfa",
  chartreuse: "#a3e635",
  mint: "#34d399",
  cyan: "#22d3ee",
  yellow: "#facc15",
};

export default function TwoRangesOverlap({
  ranges = [], // [A, B] lub [[A1,A2,...],[B1,B2,...]]
  width = 620,
  height = 170,
  showZero = true,
  axisColor = "currentColor",
  colorA = "#f59e0b",
  colorB = "#60a5fa",

  // wygląd zacienienia części wspólnych
  overlapColor = "#166534",
  overlapColorPreset,
  overlapOpacity = 0.28,
  overlapBlur = 0,

  lockPair = true,
  pairFractions = [1 / 3, 2 / 3],

  // geometra/UX
  baseOffset = 38,
  halfGap = 10,
  overlapTopPad = 4,

  // podpisy
  fontSize = 18,
  fontFamily = "KaTeX_Main, STIXGeneral, 'Times New Roman', serif",
  useMathMinus = true,
}) {
  const AR = 10,
    pinR = 7,
    strokeW = 3,
    halfW = strokeW / 2;

  const overlapFill = OVERLAP_PRESETS[overlapColorPreset] ?? overlapColor;
  const filterId = useMemo(
    () => `twoRangesOverlapBlur-${Math.random().toString(36).slice(2)}`,
    []
  );

  /* ── normalizacja wejścia: pojedyncze vs multi ─────────────────── */
  const normalizeEntry = (entry) => {
    if (!entry) return [];
    return Array.isArray(entry) ? entry.filter(Boolean) : [entry];
  };
  const Aseg = normalizeEntry(ranges[0]);
  const Bseg = normalizeEntry(ranges[1]);

  /* ── helpers: stabilne etykiety (SVG fractions) ─────────────────── */
  const normalizeMinus = (txt) =>
    useMathMinus ? String(txt ?? "").replace(/-/g, "−") : String(txt ?? "");

  const isSimpleNumericLatex = (latex) => {
    if (!latex) return false;
    const s = normalizeMinus(
      String(latex ?? "")
        .replace(/\\,/g, " ")
        .replace(/[{}]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    );
    return /^[0-9−+\-., ]+$/.test(s);
  };

  // Parsowanie:
  //  - "\\dfrac{2}{5}" / "\\frac{2}{5}"
  //  - "1\\dfrac{1}{4}" / "-3\\frac{1}{2}"
  //  - "-\\dfrac{1}{2}" / "−\\dfrac{1}{2}"  (SAM ZNAK PRZED UŁAMKIEM)
  const parseFractionLatex = (latexRaw) => {
    if (!latexRaw) return null;

    let s = String(latexRaw)
      .replace(/\\,/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // Unicode minus -> ASCII na czas parsowania
    const sParse = s.replace(/−/g, "-");

    // 1) liczba mieszana
    const mixed = sParse.match(
      /^([+\-]?\d+)\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/
    );

    let whole = null;
    let fracPart = sParse;

    if (mixed) {
      whole = mixed[1];
      fracPart = mixed[2];
    } else {
      // 2) sam znak przed ułamkiem
      const signOnly = sParse.match(
        /^([+\-])\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/
      );
      if (signOnly) {
        whole = signOnly[1];
        fracPart = signOnly[2];
      }
    }

    const frac = fracPart.match(/\\(?:d)?frac\{([^}]+)\}\{([^}]+)\}/);
    if (!frac) return null;

    let num = String(frac[1]).replace(/[{}]/g, "").trim();
    let den = String(frac[2]).replace(/[{}]/g, "").trim();

    return {
      whole: whole != null ? normalizeMinus(String(whole).trim()) : null,
      num: normalizeMinus(num),
      den: normalizeMinus(den),
    };
  };

  const approxTextW = (txt, fs) => {
    const t = String(txt ?? "");
    return Math.max(0, t.length * fs * 0.58);
  };

  const FractionSvgLabel = ({ x, y, frac, fs = fontSize }) => {
    if (!frac) return null;

    const whole = frac.whole;
    const num = frac.num;
    const den = frac.den;

    const fsWhole = fs;
    const fsFrac = Math.max(11, Math.round(fs * 0.78));

    const gap = Math.round(fs * 0.34);
    const fracPad = Math.max(6, Math.round(fs * 0.22));

    const wholeW = whole ? approxTextW(whole, fsWhole) : 0;
    const numW = approxTextW(num, fsFrac);
    const denW = approxTextW(den, fsFrac);

    const fracW = Math.max(numW, denW) + fracPad * 2;
    const totalW = whole ? wholeW + gap + fracW : fracW;

    const startX = -totalW / 2;
    const fracXCenter = startX + (whole ? wholeW + gap + fracW / 2 : fracW / 2);

    const numY = 0;
    const lineY = fsFrac * 1.05;
    const denY = fsFrac * 1.35;

    return (
      <g transform={`translate(${x} ${y})`} style={{ pointerEvents: "none" }}>
        {whole && (
          <text
            x={startX}
            y={Math.round((denY + fsFrac) / 2)}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={fsWhole}
            fontFamily={fontFamily}
            fill="currentColor"
          >
            {whole}
          </text>
        )}

        <text
          x={fracXCenter}
          y={numY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {num}
        </text>

        <line
          x1={fracXCenter - fracW / 2}
          y1={lineY}
          x2={fracXCenter + fracW / 2}
          y2={lineY}
          stroke="currentColor"
          strokeWidth={Math.max(1.6, Math.round(fs * 0.08))}
          strokeLinecap="round"
        />

        <text
          x={fracXCenter}
          y={denY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {den}
        </text>
      </g>
    );
  };

  const latexToPlain = (latex) =>
    normalizeMinus(
      String(latex ?? "")
        .replace(/\\,/g, " ")
        .replace(/[{}]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    );

  /* ── etykiety poniżej osi ──────────────────────────────────────── */
  const approxLabelWidth = (latex) => {
    const s = String(latex || "");
    const extra = s.includes("\\dfrac") || s.includes("\\frac") ? 48 : 28;
    return Math.max(90, Math.min(240, 8 * s.length + extra));
  };

  const labelYOffset = 12;
  const tallestRowH = 56;

  // maksymalna szerokość etykiet → padding boczny
  const labelWidths = [...Aseg, ...Bseg]
    .flatMap((r) =>
      r
        ? [
            approxLabelWidth(r.leftLabelLatex ?? ""),
            approxLabelWidth(r.rightLabelLatex ?? ""),
          ]
        : []
    )
    .filter((w) => w > 0);

  const maxLbl = labelWidths.length ? Math.max(...labelWidths) : 0;

  const padSide = 24 + maxLbl / 2 + 8;
  const bottomPad = 28 + labelYOffset + tallestRowH;

  /* ── geometria osi ─────────────────────────────────────────────── */
  const leftX = padSide;
  const rightX = width - padSide;
  const axisPxW = Math.max(0, rightX - leftX);
  const axisY = height - bottomPad;

  const pinTopY_A = axisY - baseOffset - halfGap;
  const pinTopY_B = axisY - baseOffset + halfGap;

  /* ── skala mapX ────────────────────────────────────────────────── */
  const finiteEnds = useMemo(() => {
    const ps = [];
    for (const r of [...Aseg, ...Bseg]) {
      if (!r) continue;
      if (Number.isFinite(r.from)) ps.push(r.from);
      if (Number.isFinite(r.to)) ps.push(r.to);
    }
    const uniq = Array.from(new Set(ps.sort((a, b) => a - b)));
    return uniq.length === 2 ? uniq : null;
  }, [Aseg, Bseg]);

  let mapX;
  if (lockPair && finiteEnds && axisPxW > 0) {
    const [v1, v2] = finiteEnds;
    const [f1, f2] = pairFractions;
    const F1 = Math.max(0, Math.min(1, f1));
    const F2 = Math.max(0, Math.min(1, f2));
    const span = v2 - v1 || 1;
    const beta = ((F2 - F1) * axisPxW) / span;
    const alpha = leftX + F1 * axisPxW - beta * v1;
    mapX = (x) => {
      if (x === -Infinity) return leftX;
      if (x === Infinity) return rightX;
      return alpha + beta * x;
    };
  } else {
    const pts = [];
    [...Aseg, ...Bseg].forEach((r) => {
      if (!r) return;
      if (Number.isFinite(r.from)) pts.push(r.from);
      if (Number.isFinite(r.to)) pts.push(r.to);
    });
    const xMin = (pts.length ? Math.min(...pts) : 0) - 3.2;
    const xMax = (pts.length ? Math.max(...pts) : 0) + 3.2;
    mapX = (x) => {
      if (x === -Infinity) return leftX;
      if (x === Infinity) return rightX;
      return leftX + ((x - xMin) / (xMax - xMin)) * axisPxW;
    };
  }

  const x0 = mapX(0);
  const zeroVisible = showZero && x0 >= leftX && x0 <= rightX;

  const spanOf = (r) => {
    const hasL = Number.isFinite(r?.from);
    const hasR = Number.isFinite(r?.to);
    const xL = hasL ? mapX(r.from) : leftX;
    const xR = hasR ? mapX(r.to) : rightX;
    return { hasL, hasR, xL, xR };
  };

  /* ── przecięcia wieloodcinkowe: wszystkie pary (Ai,Bj) ─────────── */
  const intersections = useMemo(() => {
    const out = [];
    for (const a of Aseg)
      for (const b of Bseg) {
        if (!a || !b) continue;
        const L = Math.max(
          Number.isFinite(a.from) ? a.from : -Infinity,
          Number.isFinite(b.from) ? b.from : -Infinity
        );
        const R = Math.min(
          Number.isFinite(a.to) ? a.to : Infinity,
          Number.isFinite(b.to) ? b.to : Infinity
        );

        const closedOK =
          (L === (a.from ?? -Infinity) ? !!a.leftClosed : true) &&
          (L === (b.from ?? -Infinity) ? !!b.leftClosed : true) &&
          (R === (a.to ?? Infinity) ? !!a.rightClosed : true) &&
          (R === (b.to ?? Infinity) ? !!b.rightClosed : true);

        if (L < R || (L === R && closedOK)) out.push({ L, R });
      }

    out.sort((p, q) => p.L - q.L || p.R - q.R);
    const merged = [];
    for (const cur of out) {
      if (!merged.length) {
        merged.push(cur);
        continue;
      }
      const last = merged[merged.length - 1];
      if (cur.L <= last.R) last.R = Math.max(last.R, cur.R);
      else merged.push(cur);
    }
    return merged;
  }, [Aseg, Bseg]);

  /* ── wypełnienie A∩B: wiele prostokątów ───────────────────────── */
  const OverlapAxisToTop = () => {
    if (!intersections.length) return null;
    const topY = Math.min(pinTopY_A, pinTopY_B) - Math.max(0, overlapTopPad);
    const y = Math.max(0, Math.min(topY, axisY));
    return intersections.map((iv, idx) => {
      const x1 = mapX(iv.L);
      const x2 = mapX(iv.R);
      return (
        <rect
          key={`ovl-${idx}`}
          x={Math.min(x1, x2)}
          y={y}
          width={Math.max(0, Math.abs(x2 - x1))}
          height={Math.max(0, axisY - y)}
          fill={overlapFill}
          fillOpacity={overlapOpacity}
          filter={overlapBlur > 0 ? `url(#${filterId})` : undefined}
        />
      );
    });
  };

  /* ── rysowanie pojedynczego segmentu ───────────────────────────── */
  const drawSegment = (r, color, key, pinTopY) => {
    if (!r) return null;
    const { hasL, hasR, xL, xR } = spanOf(r);
    const nodes = [];

    // ticki
    if (hasL)
      nodes.push(
        <line
          key={`tL-${key}`}
          x1={xL}
          y1={axisY - 6}
          x2={xL}
          y2={axisY + 6}
          stroke={axisColor}
          strokeWidth="2"
        />
      );
    if (hasR)
      nodes.push(
        <line
          key={`tR-${key}`}
          x1={xR}
          y1={axisY - 6}
          x2={xR}
          y2={axisY + 6}
          stroke={axisColor}
          strokeWidth="2"
        />
      );

    // piny
    if (hasL)
      nodes.push(
        <line
          key={`pinL-${key}`}
          x1={xL}
          y1={axisY}
          x2={xL}
          y2={r.leftClosed ? pinTopY : pinTopY + pinR - halfW}
          stroke={color}
          strokeWidth={strokeW}
        />
      );
    if (hasR)
      nodes.push(
        <line
          key={`pinR-${key}`}
          x1={xR}
          y1={axisY}
          x2={xR}
          y2={r.rightClosed ? pinTopY : pinTopY + pinR - halfW}
          stroke={color}
          strokeWidth={strokeW}
        />
      );

    // belka pozioma
    if (hasL && hasR) {
      const x1 = r.leftClosed ? xL : xL + pinR + halfW;
      const x2 = r.rightClosed ? xR : xR - pinR - halfW;
      nodes.push(
        <line
          key={`h-${key}`}
          x1={x1}
          y1={pinTopY}
          x2={x2}
          y2={pinTopY}
          stroke={color}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      );
    } else if (hasL && !hasR) {
      const start = r.leftClosed ? xL : xL + pinR + halfW;
      nodes.push(
        <line
          key={`h-${key}`}
          x1={start}
          y1={pinTopY}
          x2={rightX}
          y2={pinTopY}
          stroke={color}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      );
    } else if (!hasL && hasR) {
      const start = r.rightClosed ? xR : xR - pinR - halfW;
      nodes.push(
        <line
          key={`h-${key}`}
          x1={start}
          y1={pinTopY}
          x2={leftX}
          y2={pinTopY}
          stroke={color}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />
      );
    }

    // końcówki
    if (hasL)
      nodes.push(
        r.leftClosed ? (
          <circle key={`cL-${key}`} cx={xL} cy={pinTopY} r={pinR} fill={color} />
        ) : (
          <circle
            key={`cL-${key}`}
            cx={xL}
            cy={pinTopY}
            r={pinR}
            fill="none"
            stroke={color}
            strokeWidth={strokeW}
          />
        )
      );
    if (hasR)
      nodes.push(
        r.rightClosed ? (
          <circle key={`cR-${key}`} cx={xR} cy={pinTopY} r={pinR} fill={color} />
        ) : (
          <circle
            key={`cR-${key}`}
            cx={xR}
            cy={pinTopY}
            r={pinR}
            fill="none"
            stroke={color}
            strokeWidth={strokeW}
          />
        )
      );

    // etykiety (stabilnie na mobile)
    const addLabel = (latex, xp, id) => {
      if (latex == null || String(latex).trim() === "") return;

      const raw = String(latex);
      const frac = parseFractionLatex(raw);
      if (frac) {
        nodes.push(
          <FractionSvgLabel
            key={id}
            x={xp}
            y={axisY + labelYOffset + 10}
            frac={frac}
            fs={fontSize}
          />
        );
        return;
      }

      if (isSimpleNumericLatex(raw)) {
        const plain = latexToPlain(raw);
        nodes.push(
          <text
            key={id}
            x={xp}
            y={axisY + labelYOffset + 10}
            textAnchor="middle"
            dominantBaseline="hanging"
            fontSize={fontSize}
            fontFamily={fontFamily}
            fill="currentColor"
            style={{ pointerEvents: "none" }}
          >
            {plain}
          </text>
        );
        return;
      }

      const w = approxLabelWidth(raw);
      const x = Math.max(0, Math.min(width - w, xp - w / 2));
      nodes.push(
        <foreignObject
          key={id}
          x={x}
          y={axisY + labelYOffset}
          width={w}
          height={tallestRowH}
          pointerEvents="none"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              width: "100%",
              height: "100%",
              textAlign: "center",
            }}
          >
            <InlineMath math={normalizeMinus(raw)} />
          </div>
        </foreignObject>
      );
    };

    if (hasL) addLabel(r.leftLabelLatex, xL, `lblL-${key}`);
    if (hasR) addLabel(r.rightLabelLatex, xR, `lblR-${key}`);

    return <React.Fragment key={`seg-${key}`}>{nodes}</React.Fragment>;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img">
      {overlapBlur > 0 && (
        <defs>
          <filter id={filterId}>
            <feGaussianBlur stdDeviation={overlapBlur} />
          </filter>
        </defs>
      )}

      {/* Tło części wspólnych przed osią i belkami */}
      <OverlapAxisToTop />

      {/* Oś */}
      <line x1={leftX} y1={axisY} x2={rightX} y2={axisY} stroke={axisColor} strokeWidth="2" />
      <path
        d={`M ${rightX - AR} ${axisY - AR / 2} L ${rightX} ${axisY} L ${rightX - AR} ${axisY + AR / 2} Z`}
        fill={axisColor}
      />

      {/* Znacznik 0 w kadrze */}
      {zeroVisible && (
        <line x1={x0} y1={axisY - 6} x2={x0} y2={axisY + 6} stroke={axisColor} strokeWidth="2" />
      )}

      {/* Wszystkie segmenty A na górnym torze */}
      {Aseg.map((seg, i) => drawSegment(seg, colorA, `A-${i}`, pinTopY_A))}

      {/* Wszystkie segmenty B na dolnym torze */}
      {Bseg.map((seg, i) => drawSegment(seg, colorB, `B-${i}`, pinTopY_B))}
    </svg>
  );
}
